import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getDateFnsLocale } from "../../utils/dateFnsLocale";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import Dropdown from "../../components/common/Dropdown";
import TextInput from "../../components/ui/TextInput";
import CompactDateRangeField from "./components/CompactDateRangeField";
import {
    getExportUsers,
    getCompanyTracksForExport,
    exportUsersToCSV,
    ExportUser,
    ExportTrack,
} from "../../api/exportUsers";
import { useNotification } from "../../context/NotificationContext";
import styles from "./ExportUsers.module.css";

const ExportUsers: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();

    // Determine locale for date picker
    const dateLocale = getDateFnsLocale(i18n.language);

    // Data state
    const [users, setUsers] = useState<ExportUser[]>([]);
    const [tracks, setTracks] = useState<ExportTrack[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [tracksLoading, setTracksLoading] = useState<boolean>(true);
    const [exportLoading, setExportLoading] = useState<boolean>(false);

    // Filter state
    const [selectedTrack, setSelectedTrack] = useState<string>("");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [commEmailStartDate, setCommEmailStartDate] = useState<Date | null>(null);
    const [commEmailEndDate, setCommEmailEndDate] = useState<Date | null>(null);
    const [search, setSearch] = useState<string>("");
    const [searchDebounced, setSearchDebounced] = useState<string>("");
    const [hideAnonymous, setHideAnonymous] = useState<boolean>(false);

    // Format date to ISO string for API
    const formatDateForApi = (date: Date | null): string | undefined => {
        if (!date) return undefined;
        return date.toISOString();
    };

    // Pagination state
    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(15);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [selectedTrack, startDate, endDate, commEmailStartDate, commEmailEndDate, searchDebounced, hideAnonymous]);

    // Fetch tracks for filter dropdown
    useEffect(() => {
        const fetchTracks = async () => {
            try {
                setTracksLoading(true);
                const token = localStorage.getItem("token");
                if (!token) return;

                const tracksData = await getCompanyTracksForExport(token);
                setTracks(tracksData);
            } catch (error) {
                console.error("Error fetching tracks:", error);
            } finally {
                setTracksLoading(false);
            }
        };

        fetchTracks();
    }, []);

    // Fetch users
    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            if (!token) {
                showNotification({
                    message: t("errorUnauthorized") || "Unauthorized",
                    type: "error",
                });
                return;
            }

            const response = await getExportUsers({
                token,
                page,
                limit,
                trackId: selectedTrack || undefined,
                startDate: formatDateForApi(startDate),
                endDate: formatDateForApi(endDate),
                commEmailStartDate: formatDateForApi(commEmailStartDate),
                commEmailEndDate: formatDateForApi(commEmailEndDate),
                search: searchDebounced || undefined,
                hideAnonymous,
            });

            setUsers(response.users);
            setTotalCount(response.pagination.totalCount);
            setTotalPages(response.pagination.totalPages);
        } catch (error: any) {
            console.error("Error fetching users:", error);
            if (error.statusCode === 403) {
                showNotification({
                    message: t("exportUsersAddonRequired") || "Export Users addon is required",
                    type: "error",
                });
            } else {
                showNotification({
                    message: t("errorFetchingUsers") || "Error fetching users",
                    type: "error",
                });
            }
        } finally {
            setLoading(false);
        }
    }, [
        page,
        limit,
        selectedTrack,
        startDate,
        endDate,
        commEmailStartDate,
        commEmailEndDate,
        searchDebounced,
        hideAnonymous,
        showNotification,
        t,
    ]);

    // Fetch users when dependencies change
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Handle CSV export
    const handleExport = async () => {
        try {
            setExportLoading(true);
            const token = localStorage.getItem("token");
            if (!token) return;

            await exportUsersToCSV({
                token,
                trackId: selectedTrack || undefined,
                startDate: formatDateForApi(startDate),
                endDate: formatDateForApi(endDate),
                commEmailStartDate: formatDateForApi(commEmailStartDate),
                commEmailEndDate: formatDateForApi(commEmailEndDate),
                search: searchDebounced || undefined,
                hideAnonymous,
            });

            showNotification({
                message: t("exportSuccess") || "Export started successfully",
                type: "success",
            });
        } catch (error) {
            console.error("Error exporting users:", error);
            showNotification({
                message: t("exportError") || "Error exporting users",
                type: "error",
            });
        } finally {
            setExportLoading(false);
        }
    };

    // Handle reset filters
    const handleResetFilters = () => {
        setSelectedTrack("");
        setStartDate(null);
        setEndDate(null);
        setCommEmailStartDate(null);
        setCommEmailEndDate(null);
        setSearch("");
        setSearchDebounced("");
        setHideAnonymous(false);
        setPage(1);
    };

    // Track options for dropdown
    const trackOptions = useMemo(() => {
        return tracks.map((track) => ({
            value: track.id,
            label: track.name,
        }));
    }, [tracks]);

    // Calculate visible pages for pagination
    const visiblePages = useMemo(() => {
        const range: (number | string)[] = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (page > 3) range.push("...");
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                range.push(i);
            }
            if (page < totalPages - 2) range.push("...");
            range.push(totalPages);
        }

        return range;
    }, [page, totalPages]);

    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalCount);

    // Format date for display
    const formatDate = (dateString: string | null) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.head}>
                <TitleSubtitle
                    title={t("exportUsersTitle") || "Export Users"}
                    subtitle={t("exportUsersSubtitle") || "View and export user data from your company's tracks"}
                    addMargin={false}
                />
                <div className={styles.total}>
                    <b>{totalCount.toLocaleString()}</b> {t("totalUsers") || "users"}
                </div>
            </div>

            {/* Toolbar */}
            <div className={styles.toolbar}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                    <div className={`${styles.filterItem} ${styles.filterItemSearch}`}>
                        <TextInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("searchByEmailOrName") || "Search by email or name..."}
                        />
                    </div>

                    <div className={styles.filterItem}>
                        <Dropdown
                            label=""
                            value={selectedTrack}
                            options={trackOptions}
                            onChange={setSelectedTrack}
                            placeholder={t("allTracks") || "All Tracks"}
                            loading={tracksLoading}
                            error={false}
                            isWithoutMargin
                        />
                    </div>

                    <div className={styles.filterItemDate}>
                        <CompactDateRangeField
                            label={t("createdAtRange") || "Created At"}
                            start={startDate}
                            end={endDate}
                            onStartChange={setStartDate}
                            onEndChange={setEndDate}
                        />
                    </div>

                    <div className={styles.filterItemDate}>
                        <CompactDateRangeField
                            label={t("commEmailAddedRange") || "Comm. Email Added"}
                            start={commEmailStartDate}
                            end={commEmailEndDate}
                            onStartChange={setCommEmailStartDate}
                            onEndChange={setCommEmailEndDate}
                        />
                    </div>

                    <div className={styles.toggleWrapper}>
                        <label className={styles.toggleLabel}>
                            <input
                                type="checkbox"
                                className={styles.toggleCheckbox}
                                checked={hideAnonymous}
                                onChange={(e) => setHideAnonymous(e.target.checked)}
                            />
                            <span className={styles.toggleSwitch}></span>
                            {t("hideAnonymous") || "Hide anonymous"}
                        </label>
                    </div>

                    <div className={styles.spacer} />

                    <div className={styles.filtersActions}>
                        <button className={styles.resetButton} onClick={handleResetFilters}>
                            {t("resetFilters") || "Reset"}
                        </button>

                        <div className={styles.exportButtonWrapper}>
                            <button
                                className={styles.exportButton}
                                onClick={handleExport}
                                disabled={exportLoading || totalCount === 0}
                            >
                                {exportLoading ? (
                                    t("exporting") || "Exporting..."
                                ) : (
                                    <>{t("exportCSV") || "Export CSV"}</>
                                )}
                            </button>
                            <div className={styles.infoIconWrapper}>
                                <span className={styles.infoIcon}>ℹ</span>
                                <div className={styles.tooltip}>
                                    {t("exportLimitInfo") ||
                                        "Export is limited to 50,000 users. The most recent users (by creation date) are exported first. Use filters to select a specific date range if needed."}
                                </div>
                            </div>
                        </div>
                    </div>
                </LocalizationProvider>
            </div>

            {/* Table panel */}
            <div className={styles.tableContainer}>
                {loading ? (
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner}></div>
                    </div>
                ) : users.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateTitle}>{t("noUsersFound") || "No users found"}</div>
                        <div className={styles.emptyStateText}>
                            {t("noUsersFoundDescription") || "Try adjusting your filters or check back later."}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={styles.tableScroll}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t("createdAt") || "Created"}</th>
                                        <th>{t("email") || "Email"}</th>
                                        <th>{t("communicationEmail") || "Communication Email"}</th>
                                        <th>{t("communicationEmailAddedAt") || "Comm. Email Added"}</th>
                                        <th>{t("name") || "Name"}</th>
                                        <th>{t("phone") || "Phone"}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.id}>
                                            <td className={styles.dateCell}>{formatDate(user.createdAt)}</td>
                                            <td className={styles.emailCell}>{user.email}</td>
                                            <td>
                                                {user.communicationEmail ? (
                                                    <span className={styles.emailCell}>{user.communicationEmail}</span>
                                                ) : (
                                                    <span className={styles.emptyCell}>—</span>
                                                )}
                                            </td>
                                            <td className={styles.dateCell}>
                                                {user.communicationEmailAddedAt ? (
                                                    formatDate(user.communicationEmailAddedAt)
                                                ) : (
                                                    <span className={styles.emptyCell}>—</span>
                                                )}
                                            </td>
                                            <td className={styles.nameCell}>
                                                {user.firstname} {user.lastname}
                                            </td>
                                            <td>
                                                {user.phone ? user.phone : <span className={styles.emptyCell}>—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.paginationContainer}>
                            <span className={styles.paginationTotal}>
                                {t("displayedUsers") || "Displayed"} {`${startItem}–${endItem} / ${totalCount}`}
                            </span>
                            {totalPages > 1 && (
                                <div className={styles.pagination}>
                                    <button
                                        className={styles.paginationButton}
                                        disabled={page === 1}
                                        onClick={() => setPage((p) => p - 1)}
                                    >
                                        &lt;
                                    </button>
                                    {visiblePages.map((p, idx) =>
                                        p === "..." ? (
                                            <span key={`ellipsis-${idx}`} className={styles.paginationEllipsis}>
                                                ...
                                            </span>
                                        ) : (
                                            <button
                                                key={p}
                                                className={`${styles.paginationButton} ${
                                                    p === page ? styles.paginationButtonActive : ""
                                                }`}
                                                onClick={() => setPage(p as number)}
                                            >
                                                {p}
                                            </button>
                                        ),
                                    )}
                                    <button
                                        className={styles.paginationButton}
                                        disabled={page === totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                    >
                                        &gt;
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ExportUsers;
